import React, { useState } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { get, head, times } from 'lodash-es';
import Container from 'reactstrap/lib/Container';
import Button from 'reactstrap/lib/Button';
import Row from 'reactstrap/lib/Row';
import Col from 'reactstrap/lib/Col';

// Models
import { buildTypeArticlesPath, EditorialEntities, EditorialModel } from 'client/data/models/editorial';

// Utils
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { getUserFriendlyTypeName } from 'site-modules/shared/utils/upper-funnel/type-mapping';
import { IMAGE_SIZE_AND_EXT_REG } from 'client/utils/image-helpers';
import { getTypeNewsPageBreadcrumbs } from 'site-modules/editorial/utils/breadcrumbs';

// Shared components
import { Breadcrumbs } from 'site-modules/shared/components/breadcrumbs/breadcrumbs';
import { HeroImage } from 'site-modules/shared/components/hero-image/hero-image';
import { TypeArticlesPage } from 'site-modules/editorial/components/articles-cards-page/articles-cards-page';
import { PageLinksWidget } from 'site-modules/shared/components/page-links-widget/page-links-widget';
import { TreehousePromotionCheck } from 'site-modules/shared/components/treehouse-promotion/treehouse-promotion-check';
import { ReadyForEvTreehouse } from 'site-modules/shared/components/ready-for-ev/ready-for-ev';

import './type-news.scss';

const HERO_IMAGE_MAP = {
  truck: 'https://media.ed.edmunds-media.com/non-make/ot/ot_21021_600.jpg',
  ev: 'https://media.ed.edmunds-media.com/lucid/ns/lucid_ns_100521_600.jpg',
};

export const getHeroImageHref = ({ articles, type, isMobile }) =>
  (HERO_IMAGE_MAP[type] || get(head(articles), 'image', '')).replace(
    IMAGE_SIZE_AND_EXT_REG,
    classnames({ '_600.jpg': isMobile, '_1600.jpg': !isMobile })
  );

function TypeNewsUI({ articlesData = {}, isMobile = false, type = '' }) {
  const [loadedPagesNum, setLoadedPagesNum] = useState(1);

  function loadNextPage() {
    setLoadedPagesNum(pageNum => pageNum + 1);
  }

  const { totalPages, results } = articlesData;
  const titleName = getUserFriendlyTypeName(type);

  const title = `${titleName} News`;
  const subtitle = type === 'ev' ? 'Electric Car News' : `${title}`;
  const breadcrumbs = getTypeNewsPageBreadcrumbs(title);
  const heroImageHref = getHeroImageHref({ articles: results, type, isMobile });
  const heroImage = { href: heroImageHref, title: '' };

  return (
    <main id="main-content" tabIndex={-1} className="type-news">
      <HeroImage heroImage={heroImage} title={title} subtitle={`${subtitle} from Edmunds`} />
      <Container>
        <Breadcrumbs className="ps-0 mb-2" data={breadcrumbs} />
        {totalPages > 0 &&
          times(loadedPagesNum, ind => ind + 1).map(pageNum => (
            <TypeArticlesPage pageNum={pageNum} type={type} isMobile={isMobile} key={pageNum} hideCategory typeNews />
          ))}
        {loadedPagesNum < totalPages && (
          <div className="text-center mt-2_5 mb-1_5">
            <Button
              onClick={loadNextPage}
              outline
              color="primary-b"
              className="show-more-btn p-1_25"
              aria-label="Load more articles"
            >
              <span className="icon-arrow-down8 size-24" aria-hidden />
            </Button>
          </div>
        )}
        {type === 'ev' && (
          <TreehousePromotionCheck>
            <Row className="my-3_5 justify-content-center">
              <Col xs={12} md={8}>
                <div className="bg-cool-gray-90 px-1 py-1 rounded-12">
                  <ReadyForEvTreehouse
                    className="bg-white rounded-12 px-1_5 py-1_5"
                    heading="Am I Ready for an EV?"
                    creativeId="treehouse-promotion"
                  />
                </div>
              </Col>
            </Row>
          </TreehousePromotionCheck>
        )}
        <PageLinksWidget divideSections={false} title="Related information" hasTopDivider />
      </Container>
    </main>
  );
}

TypeNewsUI.propTypes = {
  type: PropTypes.string,
  isMobile: PropTypes.bool,
  articlesData: EditorialEntities.ArticlesData,
  location: PropTypes.shape({}),
};

export const mapStateToProps = ({ mobile }) => ({
  isMobile: mobile,
});

export const TypeNews = connect(mapStateToProps)(
  connectToModel(TypeNewsUI, {
    articlesData: bindToPath(({ type }) => buildTypeArticlesPath(type), EditorialModel),
  })
);
